/**
 * Instant Search Module
 *
 * Main module orchestrator for the ISW with DI container.
 *
 * **Migration from Legacy**:
 * - Legacy: handleInstantSearchWidget() function with context parameter
 * - New: Module with DI, OnApplicationBootstrap, and window.boostISWModule export
 *
 * @module instant-search.module
 */

import type { AppBlock } from '@boost/widget-integration-tae/lib';
import { Module, ModuleRefImpl } from 'framework/core';

import { WATERMARK_POWERED_BY_BOOST_URL } from '../../@deprecation/constants/url';
import {
  handleClickTagAItemBuildUrlWithLocale,
  handleFirstLoadAfterRender,
  isTabletPortraitMaxWidth,
} from '../../@deprecation/utils';
import { AppService } from '../app/app.service';
import { B2BModule } from '../b2b/b2b.module';
import { PlatformModule } from '../platform/platform.module';
import { InstantSearchAPI } from './instant-search.api';
import { InstantSearchController } from './instant-search.controller';
import { InstantSearchSelectors } from './instant-search.selectors';
import { InstantSearchService } from './instant-search.service';
import type { SearchSettings, SuggestionData, ThemeSearchConfigMap } from './instant-search.types';
import { PositionService } from './position.service';
import { RecentSearchService } from './recent-search.service';

// =============================================================================
// Window Extensions
// =============================================================================

/**
 * Window extensions for ISW module.
 * Declared here instead of types.ts to avoid circular imports.
 */
declare global {
  interface Window {
    /** ISW module instance for external access */
    boostISWModule?: InstantSearchModule;
    /** Flag to disable keyboard navigation */
    boostSdDisableISWKeyDown?: boolean;
    /** Widget integration config including custom themes */
    boostWidgetIntegrationConfig?: {
      /** Custom theme configurations */
      instantSearchThemes?: ThemeSearchConfigMap;
    };
  }
}

/**
 * Instant Search Widget Module
 *
 * Provides a unified, framework-based instant search system for Shopify themes.
 *
 * **Features**:
 * - Search-as-you-type with debounced API requests
 * - Multiple display styles (dropdown, full-width overlay)
 * - 20+ theme auto-detection and integration
 * - Full keyboard accessibility (WCAG compliant)
 * - Recent searches management
 * - Custom events for extensibility
 *
 * **Migration from Legacy**:
 *
 * | Legacy                              | New Module                          |
 * |-------------------------------------|-------------------------------------|
 * | handleInstantSearchWidget(context)  | window.boostISWModule.initISWWithContext(settings) |
 * | Global functions with context param | DI-based services                   |
 * | Hard-coded selectors                | Theme config map                    |
 * | Inline event handling               | Controller with state management    |
 *
 * **Technical Support Extension**:
 * ```javascript
 * window.boostWidgetIntegration.extend('InstantSearchModule', (InstantSearchModule) => {
 *   return class CustomISWModule extends InstantSearchModule {
 *     async onInitBlock(block) {
 *       // Add custom tracking
 *       await super.onInitBlock(block);
 *     }
 *   };
 * });
 * ```
 */
@Module({
  imports: [PlatformModule, B2BModule],
  providers: [
    InstantSearchService,
    InstantSearchAPI,
    InstantSearchSelectors,
    InstantSearchController,
    RecentSearchService,
    PositionService,
  ],
  exports: [
    InstantSearchService,
    InstantSearchAPI,
    InstantSearchSelectors,
    InstantSearchController,
    RecentSearchService,
    PositionService,
  ],
})
export class InstantSearchModule {
  /** Instance of the InstantSearchService */
  protected instantSearchService!: InstantSearchService;

  /** Instance of the InstantSearchController */
  protected instantSearchController!: InstantSearchController;

  /** Instance of the InstantSearchSelectors */
  protected instantSearchSelectors!: InstantSearchSelectors;

  /** Instance of the InstantSearchAPI */
  protected instantSearchAPI!: InstantSearchAPI;

  /** Instance of the RecentSearchService */
  protected recentSearchService!: RecentSearchService;

  /** Whether the module has been initialized with context */
  protected contextInitialized = false;

  /** Whether the block has been fully initialized (including click listeners, bundles, etc.) */
  protected blockInitialized = false;

  /** Whether services have been resolved from DI */
  protected servicesResolved = false;

  /** Current bundles from the latest search render (updated via boost-sd-isw-rendered event) */
  protected currentBundles: Array<Record<string, unknown>> = [];

  constructor(
    protected moduleRef: ModuleRefImpl,
    protected appService: AppService
  ) {
    // Follows the standard Turbo module pattern (Filter, Recommendation, Cart):
    // constructor → setupBlockListener → onInitBlock
    //
    // IMPORTANT: Accessing `this.appService` here forces the framework's
    // `createModuleWithCorrectRef` fallback path (proper DI resolution).
    // Without this access, the framework's try-block succeeds with only
    // `moduleRef` injected, leaving `appService` as `undefined`.
    this.setupBlockListener();
  }

  /**
   * Sets up the TAE App block listener for instant search widget.
   *
   * Follows the same pattern as FilterModule.setupBlockListener(),
   * RecommendationModule.setupBlockListener(), etc.
   *
   * Override this method to customize block detection or support additional block types.
   *
   * @example
   * ```javascript
   * window.boostWidgetIntegration.extend('InstantSearchModule', (ISWModule) => {
   *   return class CustomISW extends ISWModule {
   *     protected setupBlockListener() {
   *       this.appService.TAEApp.addRegistryBlockListener((block) => {
   *         if (block.blockType === 'instantSearch' || block.blockType === 'customSearch') {
   *           this.onInitBlock(block);
   *         }
   *       });
   *     }
   *   };
   * });
   * ```
   */
  protected setupBlockListener(): void {
    this.appService.TAEApp.addRegistryBlockListener((block) => {
      if (block.blockType === 'instantSearch') {
        this.onInitBlock(block);
      }
    });
  }

  /**
   * Called when an instant search block is registered.
   *
   * Resolves services, exports the module to `window`, and defers or
   * immediately initializes the controller depending on device type.
   *
   * On mobile/tablet (< 991 px) the ISW initializes immediately because
   * users interact with the search input right away. On desktop, initialization
   * is deferred until the first `mousemove` or `touchmove` event for performance.
   *
   * @param block - The AppBlock instance being initialized
   */
  protected async onInitBlock(block: AppBlock): Promise<void> {
    try {
      // 1. Always resolve services immediately
      this.resolveServices();

      // 2. Extract search settings from block context
      const blockContext = block as AppBlock & {
        templateSettings?: { searchSettings?: Record<string, unknown> };
        app?: { searchSettings?: Record<string, unknown> };
      };
      const searchSettings = (blockContext.templateSettings?.searchSettings ||
        blockContext.app?.searchSettings) as unknown as SearchSettings | undefined;

      // 4. Define the full init routine (controller + click listeners + bundles)
      const doInit = async (): Promise<void> => {
        if (this.blockInitialized) return;

        try {
          if (searchSettings) {
            await this.initISWWithContext(searchSettings);
          }

          // Set up ISW-specific click delegation
          this.setupBlockClickListeners(block);

          // Initialize bundle integrations (dynamic + volume)
          await this.initBundleIntegrations(block);

          // Call afterRender customization hooks
          this.callAfterRenderHooks(block);

          this.blockInitialized = true;

          // Clean up the lazy-init listener that didn't fire
          document.removeEventListener('mousemove', doInit);
          document.removeEventListener('touchmove', doInit);
        } catch (err) {
          this.appService.logger.error('Failed to initialize instant search block', err);
          // Retry on next interaction
          document.addEventListener('mousemove', doInit, { once: true });
          document.addEventListener('touchmove', doInit, { once: true });
        }
      };

      // 5. Lazy init: mobile/tablet inits immediately, desktop defers
      if (isTabletPortraitMaxWidth()) {
        await doInit();
      } else {
        document.addEventListener('mousemove', doInit, { once: true });
        document.addEventListener('touchmove', doInit, { once: true });
      }
    } catch (error) {
      this.appService.logger.error('Failed to initialize instant search block listener', error);
    }
  }

  /**
   * Resolve DI service instances (idempotent).
   */
  protected resolveServices(): void {
    if (this.servicesResolved) return;

    this.instantSearchService = this.moduleRef.get<InstantSearchService>(InstantSearchService);
    this.instantSearchController =
      this.moduleRef.get<InstantSearchController>(InstantSearchController);
    this.instantSearchSelectors =
      this.moduleRef.get<InstantSearchSelectors>(InstantSearchSelectors);
    this.instantSearchAPI = this.moduleRef.get<InstantSearchAPI>(InstantSearchAPI);
    this.recentSearchService = this.moduleRef.get<RecentSearchService>(RecentSearchService);

    window.boostISWModule = this;
    this.servicesResolved = true;
  }

  // ===========================================================================
  // Block integration helpers (migrated from app.js ISW block)
  // ===========================================================================

  /**
   * Set up click delegation for the ISW block.
   *
   * Handles watermark clicks, recent search suggestion clicks,
   * bundle product clicks, and locale-aware link navigation.
   *
   * @param block - The AppBlock providing the document scope
   */
  protected setupBlockClickListeners(block: AppBlock): void {
    const doc = (block as Record<string, unknown>).document as Document | undefined;

    // Track bundles from each render so click handlers have access to bundle data
    window.addEventListener('boost-sd-isw-rendered', ((e: CustomEvent) => {
      const bundles = e.detail?.data?.bundles;
      this.currentBundles = Array.isArray(bundles) ? bundles : [];
    }) as EventListener);

    (doc || document).addEventListener('click', (event: Event) => {
      const target = event.target as HTMLElement;

      // Watermark click → open Boost Commerce page
      if (target?.closest('.boost-sd__watermark-powered-by-boost')) {
        window.open(WATERMARK_POWERED_BY_BOOST_URL, '_blank')?.focus();
      }

      // Popular suggestion / no-result suggestion → save as recent search
      this.handleRecentSearchClick(event as MouseEvent);

      // Dynamic bundle popup button click (inside rendered embedded bundle)
      const showPopupTarget = target?.closest(
        '.boost-sd__recommendation-dynamic-show-popup'
      ) as HTMLElement | null;
      if (showPopupTarget) {
        const bundleElement = showPopupTarget.closest('.boost-sd__recommendation--dynamic');
        const widgetId = bundleElement?.getAttribute('data-widget-id');
        if (widgetId) {
          const bundleData = this.currentBundles.find((b) => b.widgetId === widgetId);
          window.dispatchEvent(
            new CustomEvent('boost-sd-open-dynamic-bundle', {
              detail: {
                widgetId,
                bundleData,
                blockType: 'instantSearch',
              },
            })
          );
        }
      }

      // Embedded bundle item click (suggestion list item wrapper)
      if (
        target?.closest('.boost-sd__suggestion-queries-item--embedded-bundle') &&
        !showPopupTarget
      ) {
        const bundleElement = target.closest('.boost-sd__product-bundle');
        const widgetId = bundleElement?.getAttribute('data-widget-id');
        if (widgetId) {
          const bundleData = this.currentBundles.find((b) => b.widgetId === widgetId);
          window.dispatchEvent(
            new CustomEvent('boost-sd-open-dynamic-bundle', {
              detail: {
                widgetId,
                bundleData,
                blockType: 'instantSearch',
              },
            })
          );
        }
      }

      // Volume bundle click → dispatch event for DI-based controller
      if (target?.closest('.boost-sd__suggestion-queries-item--volume-bundle')) {
        const bundleElement = target.closest('.boost-sd__volume-bundle-product-item');
        const widgetId = bundleElement?.getAttribute('data-widget-id');
        if (widgetId) {
          const bundleData = this.currentBundles.find((b) => b['widgetId'] === widgetId);
          window.dispatchEvent(
            new CustomEvent('boost-sd-open-volume-bundle', {
              detail: { widgetId, bundleData },
            })
          );
        }
      }

      // Locale-aware link navigation for suggestion/collection/search links
      // NOTE: must be last — handleClickTagAItemBuildUrlWithLocale calls setWindowLocation
      const classTagALink =
        'a.boost-sd__suggestion-queries-link, a.boost-sd__suggestion-queries-item-link';
      handleClickTagAItemBuildUrlWithLocale(event, classTagALink);
    });
  }

  /**
   * Handle clicks on popular suggestions and no-result suggestions
   * to save them as recent searches.
   *
   * @param event - The click event
   */
  protected handleRecentSearchClick(event: MouseEvent): void {
    if (!event?.target) return;
    const target = event.target as HTMLElement;

    const clickPopularSuggestion = target.closest('.boost-sd__suggestion-queries-item--suggestion');
    const clickPopularSearch = target.closest('.boost-sd__no-search-result-page-suggestion-item');

    if (clickPopularSuggestion) {
      const searchTerm = clickPopularSuggestion
        .querySelector('.boost-sd__suggestion-queries-link')
        ?.textContent?.trim();
      if (searchTerm) {
        this.addRecentSearch(searchTerm);
      }
    }

    if (clickPopularSearch) {
      const term = (clickPopularSearch as HTMLElement).textContent?.trim();
      if (term) {
        const encodedTerm = encodeURIComponent(term);
        (clickPopularSearch as HTMLAnchorElement).setAttribute('href', `/search?q=${encodedTerm}`);
        this.addRecentSearch(term);
      }
    }
  }

  /**
   * Initialize dynamic bundle and volume bundle integrations for ISW.
   *
   * Bundle modules are loaded on demand so they can register their own
   * TAE listeners only when the instant-search block is initialized.
   *
   * @param _block - The AppBlock providing context for bundle initialization
   */
  protected async initBundleIntegrations(_block: AppBlock): Promise<void> {
    await Promise.all([
      this.moduleRef.loadModule(
        () =>
          import(
            /* webpackChunkName: "predictive-bundle.module" */
            '../predictive-bundle/predictive-bundle.module'
          ).then((m) => m.PredictiveBundleModule),
        { singleton: true }
      ),
      this.moduleRef.loadModule(
        () =>
          import(
            /* webpackChunkName: "volume-bundle.module" */
            '../volume-bundle/volume-bundle.module'
          ).then((m) => m.VolumeBundleModule),
        { singleton: true }
      ),
    ]);
  }

  /**
   * Call afterRender customization hooks if configured.
   *
   * Supports both block-specific and global afterRender callbacks
   * defined via `context.app.customization`.
   *
   * @param block - The AppBlock providing customization config
   */
  protected callAfterRenderHooks(block: AppBlock): void {
    handleFirstLoadAfterRender(block as any);
  }

  /**
   * Initialize ISW with search settings.
   *
   * **Legacy**: handleInstantSearchWidget(context)
   * **New**: initISWWithContext(searchSettings)
   *
   * @param searchSettings - Search settings from context
   * @returns Promise that resolves when initialization is complete
   *
   * @example
   * ```javascript
   * const searchSettings = getSearchSettings(context);
   * await window.boostISWModule.initISWWithContext(searchSettings);
   * ```
   */
  async initISWWithContext(searchSettings: SearchSettings): Promise<void> {
    if (this.contextInitialized) {
      this.appService.logger.warn('Instant search module already initialized with context');
      return;
    }

    try {
      await this.instantSearchController.initWithContext(searchSettings);
      this.contextInitialized = true;
      this.appService.logger.info('Instant search module initialized with context');
    } catch (error) {
      this.appService.logger.error(
        'Failed to initialize instant search module with context',
        error
      );
      throw error;
    }
  }

  /**
   * Open suggestion dropdown/overlay.
   *
   * **Legacy**: openSuggestion(context, input) / onOpenSuggestion(context, input)
   * **New**: openSuggestion(input?)
   *
   * @param input - Optional input element to open for
   */
  openSuggestion(input?: HTMLInputElement): void {
    const targetInput = input || this.instantSearchSelectors.getAllSearchInputs()[0];
    if (targetInput) {
      this.instantSearchController.openSuggestion(targetInput);
    }
  }

  /**
   * Close suggestion dropdown/overlay.
   *
   * **Legacy**: closeSuggestion(context, input?) / onCloseSuggestion(context, input?)
   * **New**: closeSuggestion()
   */
  closeSuggestion(): void {
    this.instantSearchController.closeSuggestion();
  }

  /**
   * Toggle suggestion visibility.
   *
   * **Legacy**: N/A
   * **New**: toggleSuggestion()
   */
  toggleSuggestion(): void {
    this.instantSearchController.toggleSuggestion();
  }

  /**
   * Trigger a search.
   *
   * **Legacy**: requestSuggestionDebounce(context, query)
   * **New**: search(query)
   *
   * @param query - Search query
   * @returns Promise that resolves when search is complete
   */
  async search(query: string): Promise<void> {
    const state = this.instantSearchController.getState();
    if (!state.isOpen) {
      this.openSuggestion();
    }

    // Dispatch search event to trigger controller
    window.dispatchEvent(
      new CustomEvent('boost-sd-search', {
        detail: { query },
      })
    );
  }

  /**
   * Get current suggestion data.
   *
   * **Legacy**: context.useContextState('instant-search')[0]()
   * **New**: getSuggestionData()
   *
   * @returns Current suggestion data or null
   */
  getSuggestionData(): SuggestionData | null {
    return this.instantSearchController.getSuggestionData();
  }

  /**
   * Get theme search configuration.
   *
   * **Legacy**: Hard-coded selectors
   * **New**: getThemeSearchConfig()
   *
   * @returns Theme-specific search configuration
   */
  getThemeSearchConfig() {
    return this.instantSearchSelectors.getThemeSearchConfig();
  }

  /**
   * Re-bind search inputs.
   * Call this after dynamically adding new search inputs to the page.
   *
   * **Legacy**: Required manual event binding
   * **New**: bindSearchInputs()
   */
  bindSearchInputs(): void {
    this.instantSearchController.bindSearchInputs();
  }

  /**
   * Handle input change event.
   * For legacy compatibility.
   *
   * @param event - Input event
   * @param input - Input element
   */
  handleInputChange(event: Event, input: HTMLInputElement): void {
    this.instantSearchController.handleInputChange(event, input);
  }

  /**
   * Handle keydown event.
   * For legacy compatibility.
   *
   * @param event - Keyboard event
   * @param input - Input element
   */
  handleKeydown(event: KeyboardEvent, input: HTMLInputElement): void {
    this.instantSearchController.handleKeydown(event, input);
  }

  /**
   * Get recent searches.
   *
   * @param limit - Maximum number of searches to return
   * @returns Array of recent searches
   */
  getRecentSearches(limit?: number) {
    this.resolveServices();
    return this.recentSearchService.getRecentSearches(limit);
  }

  /**
   * Add a recent search.
   *
   * @param query - Search query to add
   */
  addRecentSearch(query: string): void {
    this.resolveServices();
    this.recentSearchService.addRecentSearch(query);
  }

  /**
   * Clear all recent searches.
   */
  clearRecentSearches(): void {
    this.resolveServices();
    this.recentSearchService.clearRecentSearches();
  }
}
